import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// assign a property to an utsav: pick the utsav, tick the rooms to assign
// (select-all supported), then assign. data.property = the property row.
// self-saving: closes with true on success so the parent reloads.
@Component({
  selector: 'app-assign-utsav-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './assign-utsav-dialog.component.html',
  styleUrl: './assign-utsav-dialog.component.scss',
})
export class AssignUtsavDialogComponent implements OnInit {
  utsavId = new FormControl<number | null>(null);
  utsavList: any[] = [];
  // utsavs the property already holds — shown with a label, not hidden:
  // picking one again simply adds the ticked rooms to it.
  assignedUtsavIds: number[] = [];
  rooms: any[] = [];
  selectedRoomIds: number[] = [];
  roomsLoaded: boolean = false;
  isLoading: boolean = false;

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private dialogRef: MatDialogRef<AssignUtsavDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { property: any }
  ) {}

  ngOnInit(): void {
    this.loadUtsavs();
    this.loadRooms();
  }

  // every utsav of the branch. Inactive (not yet activated) ones are listed too:
  // rooms are assigned while the utsav is being prepared, BEFORE the Central
  // Establishment Admin activates it. Already-assigned utsavs stay listed so the
  // admin can add more rooms of this property to them.
  loadUtsavs(): void {
    this.establishmentService.listUtsavs().subscribe({
      next: (res: any) => {
        this.utsavList = Array.isArray(res?.data?.utsavs) ? res.data.utsavs : [];
        this.assignedUtsavIds = (this.data.property.utsavs || []).map((u: any) => Number(u.utsavId));
      },
      error: (err) => {
        this.toastService.error(err?.error?.message || 'Failed to load utsavs.');
      },
    });
  }

  utsavLabel(utsav: any): string {
    const notes: string[] = [];
    if (Number(utsav.status) !== 1) notes.push('not activated yet');
    if (this.assignedUtsavIds.includes(Number(utsav.utsavId))) notes.push('already assigned');
    return notes.length ? `${utsav.name} (${notes.join(', ')})` : utsav.name;
  }

  // ALL rooms of the property. Inactive rooms and rooms already in the picked
  // utsav stay visible but their checkbox is disabled.
  loadRooms(): void {
    this.establishmentService.listRooms(Number(this.data.property.propertyId)).subscribe({
      next: (res: any) => {
        const rooms = res?.data?.rooms || [];
        // ids arrive as strings (bigint) — normalize once so the checkbox
        // checks (selectedRoomIds.includes) compare number to number
        this.rooms = rooms.map((r: any) => ({ ...r, roomId: Number(r.roomId) }));
        this.roomsLoaded = true;
      },
      error: (err) => {
        this.roomsLoaded = true;
        this.toastService.error(err?.error?.message || 'Failed to load rooms.');
      },
    });
  }

  private isRoomInPickedUtsav(room: any): boolean {
    const utsavId = Number(this.utsavId.value);
    if (!utsavId) return false;
    return (room.utsavs || []).some((u: any) => Number(u.utsavId) === utsavId);
  }

  isRoomDisabled(room: any): boolean {
    return Number(room.status) !== 1 || this.isRoomInPickedUtsav(room);
  }

  roomNote(room: any): string {
    if (Number(room.status) !== 1) return 'inactive';
    if (this.isRoomInPickedUtsav(room)) return 'already in this utsav';
    return '';
  }

  // rooms the current utsav pick allows: active and not already in it.
  get selectableRooms(): any[] {
    return this.rooms.filter((r: any) => !this.isRoomDisabled(r));
  }

  // picking another utsav can disable rooms that were ticked — drop those ticks.
  onUtsavChange(): void {
    const allowed = new Set(this.selectableRooms.map((r: any) => Number(r.roomId)));
    this.selectedRoomIds = this.selectedRoomIds.filter((id) => allowed.has(id));
  }

  toggleRoom(roomId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const id = Number(roomId);
    if (checked) {
      if (!this.selectedRoomIds.includes(id)) this.selectedRoomIds.push(id);
    } else {
      this.selectedRoomIds = this.selectedRoomIds.filter((x) => x !== id);
    }
  }

  // select all = every ACTIVE room not already in the picked utsav.
  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedRoomIds = checked ? this.selectableRooms.map((r: any) => Number(r.roomId)) : [];
  }

  get allSelected(): boolean {
    const selectable = this.selectableRooms;
    return selectable.length > 0 && selectable.every((r: any) => this.selectedRoomIds.includes(Number(r.roomId)));
  }

  submit(): void {
    if (!this.utsavId.value) {
      this.toastService.error('Select an utsav.');
      return;
    }
    if (this.selectedRoomIds.length === 0) {
      this.toastService.error('Select at least one room to assign.');
      return;
    }
    this.isLoading = true;
    this.establishmentService.addRoomsToUtsav(this.utsavId.value, this.selectedRoomIds).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Property assigned to the utsav.');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to assign to the utsav.');
      },
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
